import { Fade, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { AddButton, EditableBloc, FlexContainer, IconTitle, VerticalLine } from '../../components';
import { Contractor } from '../../entities';
import { useContractors, useFetch } from '../../hooks';
import useEmails, { EmailTemplate } from '../../hooks/data/useEmail';
import ContractorPanel from './components/ContractorPanel';
import NoContent from './components/NoContent';
import useStyles from './styles';
import partnerSvg from '../../assets/partners.svg';

export const Contractors = (): JSX.Element => {
  const { spacing } = useTheme();
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [isPanelVisible, setIsPanelVisible] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState<Contractor>({} as Contractor);
  const styles = useStyles();
  const contractorsRepository = useContractors();
  const emailsRepository = useEmails();
  const { data, loading, refetch } = useFetch(contractorsRepository.fetchAll);
  const { enqueueSnackbar } = useSnackbar();

  const [inProgress, setInProgress] = useState(false);

  useEffect(() => {
    setContractors(data ?? []);
  }, [data]);

  const handleSetPanelOpen = () => {
    setSelectedContractor({} as Contractor);
    setIsPanelVisible(!isPanelVisible);
  };

  const sendInvite = async (input: Contractor): Promise<void> => {
    setInProgress(true);
    const { REACT_APP_MERCADO_PAGO_AUTH_URI, REACT_APP_MERCADO_PAGO_CLIENT_ID, REACT_APP_MERCADO_PAGO_REDIRECT_URI } =
      process.env;

    const signInUrl = `${REACT_APP_MERCADO_PAGO_AUTH_URI}/authorization?client_id=${REACT_APP_MERCADO_PAGO_CLIENT_ID}&response_type=code&platform_id=mp&state=${input.id}&redirect_uri=${REACT_APP_MERCADO_PAGO_REDIRECT_URI}`;

    await emailsRepository.sendEmail({
      to: [input.contactEmail],
      template: EmailTemplate.SplitPayment,
      data: {
        vendor: input.label,
        signInUrl,
      },
    });

    setInProgress(false);
  };

  const handleSubmit = async (input: Omit<Contractor, 'id'> & { id?: string }) => {
    try {
      if (input.id) {
        await contractorsRepository.update({ ...input, id: input.id! });
      } else {
        const result = await contractorsRepository.create(input);
        await sendInvite(result);
      }

      handleSetPanelOpen();
      enqueueSnackbar('Parceiro salvo com sucesso!', { variant: 'success' });
      refetch();
    } catch (err) {
      enqueueSnackbar('Não foi possível salvar agora', { variant: 'error' });
    }
  };

  const handleSelectContractor = (contractor?: Contractor) => {
    if (contractor) {
      setSelectedContractor(contractor);
    }

    setIsPanelVisible(true);
  };

  const showNoContent = useMemo(() => !isPanelVisible && contractors.length === 0, [isPanelVisible, contractors]);

  if (loading && contractors.length === 0) {
    return <></>;
  }

  return (
    <div className={styles.container}>
      {showNoContent ? (
        <NoContent />
      ) : (
        <div className={styles.containerRight}>
          <IconTitle title="Lista de parceiros" />
          <FlexContainer>
            {contractors.map((contractor) => (
              <EditableBloc<Contractor>
                key={contractor.id}
                label={contractor.label}
                value={contractor}
                width={`calc(25% - ${spacing(1.5)})`}
                maxWidth="600px"
                minWidth="250px"
                prepend={<div className={styles.image} style={{ backgroundImage: `url(${partnerSvg})` }} />}
                onClickEdit={handleSelectContractor}
              />
            ))}
          </FlexContainer>
        </div>
      )}
      <Fade in={isPanelVisible} mountOnEnter unmountOnExit>
        <VerticalLine />
      </Fade>
      <ContractorPanel
        visible={isPanelVisible}
        contractor={selectedContractor}
        inProgress={inProgress}
        onClickClose={handleSetPanelOpen}
        onSubmit={handleSubmit}
        onSend={sendInvite}
      />
      {!isPanelVisible && <AddButton label="adicionar parceiro" onClick={() => handleSetPanelOpen()} />}
    </div>
  );
};

export default Contractors;
